// Admin Console — top-level page for /admin. Owns the org filter
// (Super Admin only) and the tab nav; each tab is its own component.
//
// Replaces web/admin.html + web/scripts/admin.js. Same endpoints, same
// data shapes; React + Tailwind in place of Bootstrap + jQuery.

import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../api/admin";
import { getSession } from "../../auth/session";
import { ActivityTab } from "./ActivityTab";
import { DatasourcesTab } from "./DatasourcesTab";
import { OverviewTab } from "./OverviewTab";
import { SessionsTab } from "./SessionsTab";
import { UsersTab } from "./UsersTab";

export type AdminTab = "overview" | "activity" | "sessions" | "users" | "datasources";

const TABS: { id: AdminTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "activity", label: "Activity" },
  { id: "sessions", label: "Sessions" },
  { id: "users", label: "Users" },
  { id: "datasources", label: "Datasources" },
];

interface Props {
  bearer: string;
}

export function AdminPage({ bearer }: Props) {
  const session = getSession();
  const role = session?.role ?? "Admin";
  const isSuperAdmin = role === "Super Admin";
  const isAdmin = role === "Admin";
  const allowed = isSuperAdmin || isAdmin;

  // Org filter — Super Admin sees a dropdown across all published orgs;
  // Org Admin is locked to their default org (mirror admin.js's
  // g_is_org_admin behaviour).
  const ownOrg = session?.defaultOrganization ?? "";
  const [organization, setOrganization] = useState<string>(isSuperAdmin ? "" : ownOrg);
  const [orgList, setOrgList] = useState<string[]>([]);

  // Active tab — backed by ?tab= search param so admins can deep-link
  // (e.g. paste a Sessions tab URL into Slack). Defaults to overview.
  const [tab, setTab] = useState<AdminTab>(() => readTabFromUrl());
  useEffect(() => {
    const onChange = () => setTab(readTabFromUrl());
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  const selectTab = (next: AdminTab) => {
    if (next === tab) return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    window.history.pushState(null, "", url.toString());
    setTab(next);
  };

  // Populate the org dropdown for Super Admin. Org Admin uses their
  // own org only — no fetch needed.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    adminApi
      .organizations(bearer, true)
      .then((orgs) => {
        if (!cancelled) setOrgList(orgs);
      })
      .catch(() => {
        /* non-fatal — empty dropdown */
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, isSuperAdmin]);

  // Org scope passed to every tab. Empty string = "all orgs" for the
  // overview endpoint (handled server-side).
  const scope = useMemo(() => ({ organization }), [organization]);

  if (!allowed) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center">
        <h1 className="text-xl font-semibold text-neutral-900">Access denied</h1>
        <p className="mt-3 text-sm text-neutral-600">
          The Admin Console is restricted to Admin and Super Admin users.
        </p>
        <a
          href="/"
          className="mt-6 inline-block rounded-full bg-brand text-white text-sm px-4 h-9 leading-9 hover:bg-brand-600"
        >
          Back to Search
        </a>
      </div>
    );
  }

  return (
    <div className="px-6 pt-6 pb-12">
      <div className="flex items-end justify-between gap-4 border-b border-line pb-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Admin Console</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Activity, sessions, users, and datasource health.
          </p>
        </div>
        {isSuperAdmin && (
          <OrgFilter
            value={organization}
            onChange={setOrganization}
            options={orgList}
          />
        )}
      </div>

      <nav className="mt-4 flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={
              "px-3 h-9 text-sm font-medium transition-colors border-b-2 -mb-px " +
              (tab === t.id
                ? "text-neutral-900 border-brand"
                : "text-neutral-500 border-transparent hover:text-neutral-800")
            }
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab bearer={bearer} scope={scope} />}
        {tab === "activity" && <ActivityTab bearer={bearer} scope={scope} />}
        {tab === "sessions" && <SessionsTab bearer={bearer} scope={scope} />}
        {tab === "users" && <UsersTab bearer={bearer} scope={scope} isSuperAdmin={isSuperAdmin} />}
        {tab === "datasources" && <DatasourcesTab bearer={bearer} scope={scope} />}
      </div>
    </div>
  );
}

function readTabFromUrl(): AdminTab {
  const raw = new URL(window.location.href).searchParams.get("tab");
  const ids: AdminTab[] = ["overview", "activity", "sessions", "users", "datasources"];
  return (ids as string[]).includes(raw ?? "") ? (raw as AdminTab) : "overview";
}

interface OrgFilterProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}

function OrgFilter({ value, onChange, options }: OrgFilterProps) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-neutral-500" htmlFor="admin-org-filter">
        Organization
      </label>
      <select
        id="admin-org-filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 px-2 pr-7 text-sm rounded-md border border-line bg-white text-neutral-800 hover:bg-chip focus:outline-none focus:ring-1 focus:ring-brand"
      >
        <option value="">All orgs</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
